import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { 
  Home, 
  Users, 
  Send, 
  Workflow,
  BarChart3, 
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  UsersIcon,
  Reply,
  User,
  Inbox,
} from 'lucide-react';

import { Badge } from '../ui/Badge';
import { useAppContext } from '../../contexts/AppContext';
import { useAuth } from '../../contexts/AuthContext';

// Custom icon: three squares and a plus (common integrations glyph)
const AppsPlusIcon: React.FC<React.SVGProps<SVGSVGElement>> = ({ className, ...props }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <path d="M17.5 15.5v4" />
    <path d="M15.5 17.5h4" />
  </svg>
);

interface NavigationItem {
  name: string;
  href?: string;
  icon: React.ComponentType<any>;
  children?: NavigationItem[];
  badge?: string;
  beta?: boolean;
}

const navigation: NavigationItem[] = [
  { name: 'Dashboard', href: '/', icon: Home },
  { name: 'Inbox', href: '/inbox', icon: Inbox },
  { 
    name: 'Audience', 
    icon: Users,
    children: [
      { name: 'Contacts', href: '/contacts', icon: Users },
      { name: 'Groups', href: '/groups', icon: UsersIcon },
    ]
  },
  { name: 'Campaigns', href: '/campaigns', icon: Send },
  { name: 'Flows', href: '/flows', icon: Workflow, beta: true },
  { name: 'Automatic Replies', href: '/automatic-replies', icon: Reply },
  { name: 'Analytics', href: '/analytics', icon: BarChart3 },
  { name: 'Integrations', href: '/integrations', icon: AppsPlusIcon, badge: 'Coming' },
];

export const Sidebar: React.FC = () => {
  const { state } = useAppContext();
  const { state: authState } = useAuth();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('sidebar-collapsed') === 'true';
    } catch {
      return false;
    }
  });

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('sidebar-collapsed', String(next)); } catch {}
      return next;
    });
  };

  const toggleExpanded = (itemName: string) => {
    if (collapsed) {
      setCollapsed(false);
      try { localStorage.setItem('sidebar-collapsed', 'false'); } catch {}
      setExpandedItems([itemName]);
      return;
    }
    setExpandedItems(prev =>
      prev.includes(itemName)
        ? prev.filter(name => name !== itemName)
        : [...prev, itemName]
    );
  };

  const renderNavigationItem = (item: NavigationItem) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.name);

    if (hasChildren) {
      return (
        <div key={item.name}>
          <button
            onClick={() => toggleExpanded(item.name)}
            title={collapsed ? item.name : undefined}
            className="group flex items-center w-full px-2 py-2 text-sm font-medium rounded-md transition-colors text-left text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <item.icon className={`flex-shrink-0 h-5 w-5 text-muted-foreground group-hover:text-foreground ${collapsed ? '' : 'mr-3'}`} />
            {!collapsed && (
              <>
                <span className="flex-1">{item.name}</span>
                {isExpanded ? (
                  <ChevronDown className="ml-2 h-4 w-4" />
                ) : (
                  <ChevronRight className="ml-2 h-4 w-4" />
                )}
              </>
            )}
          </button>

          {!collapsed && isExpanded && (
            <div className="ml-6 mt-1 space-y-1">
              {item.children!.map((child) => (
                <NavLink
                  key={child.name}
                  to={child.href!}
                  className="group flex items-center px-2 py-1.5 text-sm font-medium rounded-md transition-colors text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  {child.name}
                </NavLink>
              ))}
            </div>
          )}
        </div>
      );
    } else if (item.badge) {
      return (
        <div
          key={item.name}
          title={collapsed ? item.name : undefined}
          className="flex items-center px-2 py-2 text-sm font-medium rounded-md cursor-not-allowed text-muted-foreground/50"
        >
          <item.icon className={`flex-shrink-0 h-5 w-5 text-muted-foreground/50 ${collapsed ? '' : 'mr-3'}`} />
          {!collapsed && (
            <>
              <span className="flex-1">{item.name}</span>
              <Badge size="sm" variant="default">{item.badge}</Badge>
            </>
          )}
        </div>
      );
    } else {
      return (
        <NavLink
          key={item.name}
          to={item.href!}
          title={collapsed ? item.name : undefined}
          className="group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <item.icon className={`flex-shrink-0 h-5 w-5 text-muted-foreground group-hover:text-foreground ${collapsed ? '' : 'mr-3'}`} />
          {!collapsed && (
            <>
              <span className="flex-1">{item.name}</span>
              {item.beta && (
                <Badge size="sm" variant="primary" className="ml-2">Beta</Badge>
              )}
            </>
          )}
        </NavLink>
      );
    }
  };

  return (
    <div
      className={`relative flex flex-col h-full bg-secondary border-r border-border transition-all duration-200 ${collapsed ? 'w-14' : 'w-64'}`}
    >
      {/* Floating collapse toggle — straddles the right border */}
      <button
        onClick={toggleCollapsed}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        className="absolute top-1/2 -translate-y-1/2 -right-3 z-10 w-6 h-6 rounded-full bg-background border border-border shadow-sm flex items-center justify-center hover:bg-muted transition-colors cursor-pointer"
      >
        {collapsed
          ? <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          : <ChevronLeft className="h-3.5 w-3.5 text-muted-foreground" />
        }
      </button>
      <div className="flex-1 flex flex-col pt-5 overflow-y-auto overflow-x-hidden">
        <nav className="mt-5 flex-1 px-2 space-y-1">
          {navigation.map(renderNavigationItem)}
        </nav>
      </div>

      {/* Profile Section */}
      <div className="border-t border-border p-3 flex-shrink-0 space-y-2">
        {(() => {
          const authUser = authState.user;
          const appUser = state.user;
          // Prefer AppContext name (updated by AccountSection after profile save),
          // fall back to AuthContext fields which are populated immediately on login/verify.
          const displayName =
            appUser?.name ||
            (authUser?.firstName || authUser?.lastName
              ? `${authUser.firstName ?? ''} ${authUser.lastName ?? ''}`.trim()
              : null);
          const displayEmail = appUser?.email || authUser?.email;

          return (
            <NavLink
              to="/profile"
              title={collapsed ? (displayName || 'Profile') : undefined}
              className="flex items-center hover:bg-muted transition-colors group rounded-md p-1.5"
            >
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-primary-foreground" />
                </div>
              </div>
              {!collapsed && (
                <div className="ml-3 flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {displayName || (authState.loading ? '…' : 'User')}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {displayEmail || (authState.loading ? '' : '')}
                  </p>
                </div>
              )}
            </NavLink>
          );
        })()}
      </div>
    </div>
  );
};